import { TPreparedClaim, TClaim } from "../model/types";

function findClaimPosition(claimMarkdown: string, chapterContent: string): number {
  if (!claimMarkdown || !chapterContent) return -1;

  let position = chapterContent.toLowerCase().indexOf(claimMarkdown.toLowerCase());

  if (position !== -1) {
    return position;
  }

  const cleanedClaim = claimMarkdown.replace(/\s+/g, " ").trim().toLowerCase();

  const cleanedContent = chapterContent.replace(/\s+/g, " ").toLowerCase();

  position = cleanedContent.indexOf(cleanedClaim);

  if (position !== -1) {
    return position;
  }

  if (claimMarkdown.includes("|")) {
    const cells = claimMarkdown
      .split("|")
      .map((cell) => cell.trim())
      .filter((cell) => cell.length > 2);

    for (const cell of cells) {
      const cellPosition = chapterContent.toLowerCase().indexOf(cell.toLowerCase());
      if (cellPosition !== -1) {
        return cellPosition;
      }
    }
  }

  const words = claimMarkdown.split(/\s+/).filter((w) => w.length > 2);
  if (words.length > 0) {
    const firstWords = words.slice(0, 3).join(" ").toLowerCase();
    position = chapterContent.toLowerCase().indexOf(firstWords);
    if (position !== -1) {
      return position;
    }
  }

  return -1;
}

function sortClaimsByPosition(claims: TPreparedClaim[], chapterContent?: string) {
  if (!chapterContent) return claims;

  const claimsWithPositions = claims.map((claim) => ({
    claim,
    position: findClaimPosition(claim.claimMarkdown, chapterContent)
  }));

  claimsWithPositions.sort((a, b) => {
    if (a.position === -1 && b.position === -1) return 0;
    if (a.position === -1) return 1;
    if (b.position === -1) return -1;
    return a.position - b.position;
  });

  return claimsWithPositions.map((item) => item.claim);
}

function sortClaimsByReviewedDate(claims: TPreparedClaim[]) {
  return claims.sort((a, b) => {
    const dateA = a.reviewedAt ? new Date(a.reviewedAt).getTime() : 0;
    const dateB = b.reviewedAt ? new Date(b.reviewedAt).getTime() : 0;
    return dateB - dateA;
  });
}

function truncate(s: string, n = 40) {
  return s.length > n ? s.slice(0, n - 3) + "..." : s;
}

const colorMap = {
  red: "bg-red-50 text-[#A73528] border border-[#A73528]",
  yellow: "border-[#B8842E] bg-[#FCF5EB] text-[#B8842E]",
  green: "bg-green-50 text-innoveraGreen-700 border border-innoveraGreen-700"
};

function getMessages(language: string) {
  const isJapanese = language === "japanese";

  return {
    first: {
      status: isJapanese ? "競合" : "Conflict",
      rationale: isJapanese ? "404を解決する必要があります。" : "Need to resolve 404.",
      styles: colorMap.red
    },
    firstSemiPlus: {
      status: isJapanese ? "証拠なし" : "No evidence",
      rationale: isJapanese
        ? "この主張は引用されておらず、裏付けるソースもありません。"
        : "The claim is not cited and no sources support it.",
      styles: colorMap.red
    },
    second: {
      status: isJapanese ? "反証済み" : "Contradicted",
      rationale: (claim: TClaim) => {
        const { length } = claim.evidence.filter((e) => e.evidenceStrength.startsWith("contradicting_"));
        if (isJapanese) {
          return length === 1
            ? "虚偽の主張 - 矛盾するソースが見つかりました。"
            : `虚偽の主張 - ${length}件の矛盾するソースが見つかりました。`;
        }
        return length === 1
          ? "False claim - found a contradicting source."
          : `False claim - found ${length} contradicting sources.`;
      },
      styles: colorMap.red
    },
    third: {
      status: isJapanese ? "競合" : "Conflict",
      rationale: isJapanese
        ? "争点あり - 支持と矛盾を解決する必要があります。"
        : "Disputed - Need to resolve support vs contradict.",
      styles: colorMap.red
    },
    fourth: {
      status: isJapanese ? "裏付けなし" : "Unsupported",
      rationale: isJapanese
        ? "幻覚の可能性あり - 肯定・否定の証拠がありません。"
        : "Possibly Hallucinated - No evidence for or against.",
      styles: colorMap.red
    },
    fifth: {
      status: isJapanese ? "裏付けあり" : "Supported",
      rationale: (claim: TClaim) => {
        const arr = claim.evidence.filter((e) => ["medium", "weak"].includes(e.evidenceStrength));
        const { length } = arr;
        const firstClaim = claim.evidence[0];
        if (isJapanese) {
          return length === 1
            ? `${truncate(firstClaim.sourceId)}がこの主張を支持しています。`
            : `${truncate(firstClaim.sourceId)}と他${length - 1}件がこの主張を支持しています。`;
        }
        return length === 1
          ? `${truncate(firstClaim.sourceId)} supports this claim.`
          : `${truncate(firstClaim.sourceId)} and ${length - 1} more support this claim.`;
      },
      styles: colorMap.yellow
    },
    sixth: {
      status: isJapanese ? "強く裏付けられた" : "Strongly Supported",
      rationale: (claim: TClaim) => {
        const arr = claim.evidence.filter((e) => ["strong", "medium", "weak"].includes(e.evidenceStrength));
        const { length } = arr;
        const firstClaim = arr[0];
        if (isJapanese) {
          return length === 1
            ? `${truncate(firstClaim.sourceId)}がこの主張を強く支持しています。`
            : `${truncate(firstClaim.sourceId)}と他${length - 1}件がこの主張を強く支持しています。`;
        }
        return length === 1
          ? `${truncate(firstClaim.sourceId)} supports this claim.`
          : `${truncate(firstClaim.sourceId)} and ${length - 1} more support this claim.`;
      },
      styles: colorMap.green
    }
  };
}

export function prepareClaims(claims: TClaim[], chapterContent?: string, language = "english") {
  const m = getMessages(language);
  const red: TPreparedClaim[] = [];
  const yellow: TPreparedClaim[] = [];
  const green: TPreparedClaim[] = [];
  const reviewed: TPreparedClaim[] = [];

  claims.forEach((claim) => {
    const { isReviewed } = claim;

    const isFirst = claim.evidence.some((e) => e.sourceMatchesMemo && e.reasoning.includes("404"));
    const isFirstSemiPlus = claim.evidence.every((e) => e.evidenceStrength === "none") && !claim.sourceCitedInMemo;
    const isSecond =
      claim.evidence.every((e) => !["strong", "medium", "weak"].includes(e.evidenceStrength)) &&
      claim.evidence.some((e) => e.evidenceStrength.startsWith("contradicting_"));
    const isThird =
      claim.evidence.some((e) => ["strong", "medium", "weak"].includes(e.evidenceStrength)) &&
      claim.evidence.some((e) => e.evidenceStrength.startsWith("contradicting_"));
    const isFourth = claim.evidence.every((e) => e.evidenceStrength === "none");
    const isFifth = claim.evidence.every((e) => ["medium", "weak"].includes(e.evidenceStrength));
    const isSixth =
      claim.evidence.some((e) => e.evidenceStrength === "strong") &&
      !claim.evidence.some((e) => e.evidenceStrength.startsWith("contradicting_"));

    switch (true) {
      case isFirst: {
        const arr = isReviewed ? reviewed : red;
        arr.push({ ...claim, ...m.first });
        break;
      }
      case isFirstSemiPlus: {
        const arr = isReviewed ? reviewed : red;
        arr.push({ ...claim, ...m.firstSemiPlus });
        break;
      }
      case isSecond: {
        const arr = isReviewed ? reviewed : red;
        const { rationale: r, ...rest } = m.second;
        const rationale = typeof r === "function" ? r(claim) : r;
        arr.push({ ...claim, rationale, ...rest });
        break;
      }
      case isThird: {
        const arr = isReviewed ? reviewed : red;
        arr.push({ ...claim, ...m.third });
        break;
      }
      case isFourth: {
        const arr = isReviewed ? reviewed : red;
        arr.push({ ...claim, ...m.fourth });
        break;
      }
      case isFifth: {
        const arr = isReviewed ? reviewed : yellow;
        const { rationale: r, ...rest } = m.fifth;
        const rationale = typeof r === "function" ? r(claim) : r;
        arr.push({ ...claim, rationale, ...rest });
        break;
      }
      case isSixth: {
        const arr = isReviewed ? reviewed : green;
        const { rationale: r, ...rest } = m.sixth;
        const rationale = typeof r === "function" ? r(claim) : r;
        arr.push({ ...claim, rationale, ...rest });
        break;
      }
    }
  });

  return {
    red: sortClaimsByPosition(red, chapterContent),
    yellow: sortClaimsByPosition(yellow, chapterContent),
    green: sortClaimsByPosition(green, chapterContent),
    reviewed: sortClaimsByReviewedDate(reviewed)
  };
}
